import type { BillingLineKind, BillingTool } from "../../shared/billing-schemas";
import { Button } from "../components/ui/button";
import { SelectField, TextField } from "./billing-fields";
import {
  blankLine,
  lineField,
  type DiscountKind,
  type LineDraft,
  type TermsDraft,
} from "./billing-terms-draft";
import { fieldState, type FieldState, type FormErrors } from "./billing-validation";

/**
 * The commercial fields of a terms version (ADR-0073), shared by the one quote form (ADR-0077).
 * The arithmetic and validation live in `billing-terms-draft.ts`; this file is the fields.
 */

type Fields = { errors: FormErrors; requirements: Record<string, string> };

type LineProps = Fields & { line: LineDraft; onChange: (line: LineDraft) => void };

/** The commercial fields are shared with the quote form (ADR-0075), which extends the same draft. */
export type DraftProps<TDraft extends TermsDraft = TermsDraft> = Fields & {
  draft: TDraft;
  update: (patch: Partial<TDraft>) => void;
};

const DISCOUNT_KINDS = [
  { value: "", label: "None" },
  { value: "percent", label: "Percent" },
  { value: "fixed", label: "Fixed amount" },
];

function lineState(props: LineProps, name: string): FieldState {
  const field = lineField(props.line.key, name);
  return fieldState(props.errors, props.requirements, field, field);
}

function LineDiscountFields(props: LineProps) {
  const { line, onChange } = props;
  return (
    <>
      <SelectField
        {...lineState(props, "discountType")}
        label="Discount"
        value={line.discountType}
        options={DISCOUNT_KINDS}
        onChange={(discountType) => {
          onChange({ ...line, discountType: discountType as DiscountKind });
        }}
      />
      {line.discountType !== "" ? (
        <TextField
          {...lineState(props, "discountValue")}
          label={line.discountType === "percent" ? "Discount %" : "Discount (USD)"}
          inputMode="decimal"
          value={line.discountValue}
          onChange={(discountValue) => {
            onChange({ ...line, discountValue });
          }}
        />
      ) : null}
    </>
  );
}

function LineAmountFields(props: LineProps) {
  const { line, onChange } = props;
  return (
    <>
      <TextField
        {...lineState(props, "amount")}
        className="sm:col-span-2"
        label="Unit amount (USD)"
        inputMode="decimal"
        value={line.amount}
        required
        onChange={(amount) => {
          onChange({ ...line, amount });
        }}
      />
      <TextField
        {...lineState(props, "quantity")}
        label="Quantity"
        inputMode="numeric"
        value={line.quantity}
        onChange={(quantity) => {
          onChange({ ...line, quantity });
        }}
      />
      {line.kind === "recurring" ? (
        <SelectField
          {...lineState(props, "cadence")}
          label="Cadence"
          value={line.cadence}
          options={[
            { value: "month", label: "Monthly" },
            { value: "year", label: "Annual" },
          ]}
          onChange={(cadence) => {
            onChange({ ...line, cadence: cadence === "year" ? "year" : "month" });
          }}
        />
      ) : null}
    </>
  );
}

function LineEditor(props: LineProps & { index: number; onRemove: () => void }) {
  const { line, index, onChange, onRemove } = props;
  return (
    <li
      className="grid gap-2 rounded-sm border p-3 sm:grid-cols-6"
      aria-label={`Line ${index + 1}`}
    >
      <SelectField
        {...lineState(props, "kind")}
        className="sm:col-span-2"
        label="Kind"
        value={line.kind}
        options={[
          { value: "one_time", label: "One-time charge" },
          { value: "recurring", label: "Recurring charge" },
          { value: "credit", label: "Credit" },
        ]}
        onChange={(value) => {
          const kind = value as BillingLineKind;
          onChange({ ...line, kind, discountType: kind === "credit" ? "" : line.discountType });
        }}
      />
      <TextField
        {...lineState(props, "description")}
        className="sm:col-span-4"
        label="Description"
        value={line.description}
        required
        maxLength={200}
        onChange={(description) => {
          onChange({ ...line, description });
        }}
      />
      <LineAmountFields {...props} />
      {line.kind !== "credit" ? <LineDiscountFields {...props} /> : null}
      <div className="flex items-end sm:col-span-6">
        <Button type="button" variant="outline" size="sm" onClick={onRemove}>
          Remove line {index + 1}
        </Button>
      </div>
    </li>
  );
}

export function TitleFields({
  draft,
  tools,
  revising,
  update,
  errors,
  requirements,
}: DraftProps & { tools: BillingTool[]; revising: boolean }) {
  return (
    <>
      <TextField
        {...fieldState(errors, requirements, "title", "terms-title")}
        label="Title"
        value={draft.title}
        required
        maxLength={160}
        onChange={(title) => {
          update({ title });
        }}
      />
      <SelectField
        {...fieldState(errors, requirements, "tool", "terms-tool")}
        label="Tool"
        value={draft.toolId}
        disabled={revising}
        options={[
          { value: "", label: "Whole organization (no single tool)" },
          ...tools.map((tool) => ({ value: tool.id, label: tool.displayName })),
        ]}
        onChange={(toolId) => {
          update({ toolId });
        }}
      />
    </>
  );
}

export function DateFields({ draft, update, errors, requirements }: DraftProps) {
  return (
    <>
      <TextField
        {...fieldState(errors, requirements, "effective", "terms-effective")}
        label="Effective from"
        type="date"
        value={draft.effective}
        required
        onChange={(effective) => {
          update({ effective });
        }}
      />
      <TextField
        {...fieldState(errors, requirements, "ends", "terms-ends")}
        label="Ends on (optional)"
        type="date"
        value={draft.ends}
        onChange={(ends) => {
          update({ ends });
        }}
      />
    </>
  );
}

export function TermsDiscountFields({ draft, update, errors, requirements }: DraftProps) {
  return (
    <>
      <SelectField
        {...fieldState(errors, requirements, "discountType", "terms-discount-type")}
        label="Terms discount"
        value={draft.discountType}
        options={[
          { value: "", label: "None" },
          { value: "percent", label: "Percent, on every charge" },
          { value: "fixed", label: "Fixed amount, off the one-time charges" },
        ]}
        onChange={(discountType) => {
          update({ discountType: discountType as DiscountKind });
        }}
      />
      {draft.discountType !== "" ? (
        <TextField
          {...fieldState(errors, requirements, "discountValue", "terms-discount-value")}
          label={draft.discountType === "percent" ? "Discount %" : "Discount (USD)"}
          inputMode="decimal"
          value={draft.discountValue}
          onChange={(discountValue) => {
            update({ discountValue });
          }}
        />
      ) : null}
    </>
  );
}

export function LinesEditor({ draft, update, errors, requirements }: DraftProps) {
  const add = (kind: BillingLineKind) => {
    update({ lines: [...draft.lines, blankLine(kind)] });
  };
  return (
    <>
      <ul className="space-y-2" aria-label="Lines">
        {draft.lines.map((line, index) => (
          <LineEditor
            key={line.key}
            line={line}
            index={index}
            errors={errors}
            requirements={requirements}
            onChange={(next) => {
              for (const name of Object.keys(next) as (keyof LineDraft)[]) {
                if (next[name] !== line[name]) errors.clear(lineField(line.key, name));
              }
              update({
                lines: draft.lines.map((candidate) =>
                  candidate.key === next.key ? next : candidate,
                ),
              });
            }}
            onRemove={() => {
              update({ lines: draft.lines.filter((candidate) => candidate.key !== line.key) });
            }}
          />
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        {(
          [
            ["one_time", "Add one-time charge"],
            ["recurring", "Add recurring charge"],
            ["credit", "Add credit"],
          ] as const
        ).map(([kind, label]) => (
          <Button
            key={kind}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              add(kind);
            }}
          >
            {label}
          </Button>
        ))}
      </div>
    </>
  );
}
